import React, { Component, Fragment } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Modal,
  notification,
  Alert,
  Form,
  Input,
  InputNumber,
  Switch,
  Row,
  Col,
} from "antd";
import {
  SyncOutlined,
  EditOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import {
  fetchElectricityPackagesAPI,
  updateElectricityPackageAPI,
  syncElectricityPackagesAPI,
} from "../../Graphs/Report/electricityAPI";
import "./Electricity.scss";

export class ElectricityPackages extends Component {
  constructor(props) {
    super(props);
    this.state = {
      packages: [],
      isLoading: false,
      isSyncing: false,
      isEditModalOpen: false,
      editingPackage: null,
      isSaving: false,
      formValues: {
        denomination: 0,
        name: "",
        arabic_name: "",
        cost_price: 0,
        sell_price: 0,
        is_active: false,
      },
      validationError: "",
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadPackages();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback);
    }
  };

  loadPackages = async () => {
    try {
      this.safeSetState({ isLoading: true });
      const { authToken } = this.props;
      const res = await fetchElectricityPackagesAPI(authToken);

      if (res && res.success && Array.isArray(res.packages)) {
        this.safeSetState({ packages: res.packages });
      } else if (res && Array.isArray(res.data)) {
        this.safeSetState({ packages: res.data });
      } else {
        this.safeSetState({ packages: [] });
      }
    } catch (err) {
      console.error("[ElectricityPackages] loadPackages error:", err);
      notification.error({
        message: "Failed to load electricity packages",
        description: err.message || "Network error",
      });
    } finally {
      this.safeSetState({ isLoading: false });
    }
  };

  handleSyncFromProvider = async () => {
    try {
      this.safeSetState({ isSyncing: true });
      const { authToken } = this.props;
      const res = await syncElectricityPackagesAPI(authToken);

      if (res && res.success) {
        notification.success({
          message: "Sync Complete",
          description: res.message || "Upstream packages synchronized successfully.",
        });
        await this.loadPackages();
      } else {
        notification.error({
          message: "Sync Failed",
          description: res?.message || "Failed to sync packages from provider.",
        });
      }
    } catch (err) {
      console.error("[ElectricityPackages] handleSyncFromProvider error:", err);
      notification.error({
        message: "Sync Error",
        description: err.message || "An error occurred while contacting the provider.",
      });
    } finally {
      this.safeSetState({ isSyncing: false });
    }
  };

  handleOpenEditModal = (record) => {
    this.safeSetState({
      isEditModalOpen: true,
      editingPackage: record,
      validationError: "",
      formValues: {
        denomination: Number(record.denomination || 0),
        name: record.name || "",
        arabic_name: record.arabic_name || "",
        cost_price: Number(record.cost_price || 0),
        sell_price: Number(record.sell_price || 0),
        is_active: Boolean(record.is_active),
      },
    });
  };

  handleCloseEditModal = () => {
    this.safeSetState({
      isEditModalOpen: false,
      editingPackage: null,
      validationError: "",
    });
  };

  handleSavePackage = async () => {
    const { formValues, editingPackage } = this.state;
    const { authToken } = this.props;

    const costPrice = Number(formValues.cost_price);
    const sellPrice = Number(formValues.sell_price);
    const denomination = Number(formValues.denomination);

    if (isNaN(costPrice) || costPrice < 0) {
      this.safeSetState({ validationError: "Cost price must be a valid non-negative number." });
      return;
    }
    if (isNaN(sellPrice) || sellPrice < 0) {
      this.safeSetState({ validationError: "Sell price must be a valid non-negative number." });
      return;
    }
    if (isNaN(denomination) || denomination <= 0) {
      this.safeSetState({ validationError: "Denomination must be a positive number." });
      return;
    }

    // Strict rule: sell_price >= cost_price when package is activated
    if (formValues.is_active && sellPrice < costPrice) {
      this.safeSetState({
        validationError: "Sell price cannot be less than cost price for active packages.",
      });
      return;
    }

    try {
      this.safeSetState({ isSaving: true, validationError: "" });
      const payload = {
        name: formValues.name.trim(),
        arabic_name: formValues.arabic_name.trim(),
        denomination,
        cost_price: parseFloat(costPrice.toFixed(3)),
        sell_price: parseFloat(sellPrice.toFixed(3)),
        is_active: formValues.is_active ? 1 : 0,
      };

      const res = await updateElectricityPackageAPI(authToken, editingPackage.id, payload);

      if (res && res.success) {
        notification.success({
          message: "Package Updated",
          description: `Package '${formValues.name}' updated successfully.`,
        });
        this.handleCloseEditModal();
        await this.loadPackages();
      } else {
        this.safeSetState({
          validationError: res?.message || "Failed to update package.",
        });
      }
    } catch (err) {
      console.error("[ElectricityPackages] handleSavePackage error:", err);
      this.safeSetState({
        validationError: err.message || "Failed to update package.",
      });
    } finally {
      this.safeSetState({ isSaving: false });
    }
  };

  render() {
    const {
      packages,
      isLoading,
      isSyncing,
      isEditModalOpen,
      editingPackage,
      isSaving,
      formValues,
      validationError,
    } = this.state;

    const totalPackages = packages.length;
    const activePackages = packages.filter((p) => p.is_active).length;
    const inactivePackages = totalPackages - activePackages;

    const columns = [
      {
        title: "ID",
        dataIndex: "package_id",
        key: "package_id",
        width: 100,
        render: (text) => <span className="mono-text">#{text}</span>,
      },
      {
        title: "Package Name",
        key: "name",
        render: (_, record) => (
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            {record.arabic_name && (
              <div style={{ fontSize: 12, color: "#8c8c8c", direction: "rtl", textAlign: "left" }}>
                {record.arabic_name}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Denomination",
        dataIndex: "denomination",
        key: "denomination",
        align: "right",
        render: (val) => `${Number(val || 0).toFixed(0)} LYD`,
      },
      {
        title: "Cost Price (Admin)",
        dataIndex: "cost_price",
        key: "cost_price",
        align: "right",
        render: (val) => (
          <span style={{ color: "#595959", fontFamily: "monospace" }}>
            {Number(val || 0).toFixed(3)} LYD
          </span>
        ),
      },
      {
        title: "Selling Price",
        dataIndex: "sell_price",
        key: "sell_price",
        align: "right",
        render: (val) => (
          <span style={{ fontWeight: 600, color: "#1890ff", fontFamily: "monospace" }}>
            {Number(val || 0).toFixed(3)} LYD
          </span>
        ),
      },
      {
        title: "Profit Margin",
        key: "profit_margin",
        align: "right",
        render: (_, record) => {
          const cost = Number(record.cost_price || 0);
          const sell = Number(record.sell_price || 0);
          const margin = sell - cost;
          const isNegative = margin < 0;
          return (
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: isNegative ? "#ff4d4f" : "#52c41a",
              }}
            >
              {margin.toFixed(3)} LYD
            </span>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "is_active",
        key: "is_active",
        align: "center",
        render: (isActive) =>
          isActive ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Active
            </Tag>
          ) : (
            <Tag color="error" icon={<CloseCircleOutlined />}>
              Inactive
            </Tag>
          ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        width: 110,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => this.handleOpenEditModal(record)}
          >
            Edit
          </Button>
        ),
      },
    ];

    return (
      <Fragment>
        <Breadcrumb title="Electricity Packages" parent="Electricity" child="Packages" url="/dashboard" />
        <div className="container-fluid electricity-page">
          {/* Header Card */}
          <Card className="electricity-header-card" bordered={false}>
            <div className="header-content">
              <div className="title-section">
                <h4>
                  <ThunderboltOutlined style={{ color: "#faad14" }} />
                  Khales GECOL Electricity Packages
                </h4>
                <p>
                  Manage recharge denominations, merchant selling prices, and upstream cost tariffs.
                </p>
              </div>
              <div className="header-actions">
                <Button
                  icon={<SyncOutlined spin={isSyncing} />}
                  onClick={this.handleSyncFromProvider}
                  loading={isSyncing}
                >
                  Sync from Provider
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={this.loadPackages}
                  loading={isLoading}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </Card>

          {/* KPI Summary Row */}
          <div className="kpi-row">
            <div className="kpi-card">
              <div className="kpi-icon-wrap primary">
                <ThunderboltOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Total Packages</div>
                <div className="kpi-value">{totalPackages}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap success">
                <CheckCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Active Packages</div>
                <div className="kpi-value">{activePackages}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap danger">
                <CloseCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Inactive Packages</div>
                <div className="kpi-value">{inactivePackages}</div>
              </div>
            </div>
          </div>

          {/* Packages Table */}
          <Card className="table-card" bordered={false}>
            <Table
              dataSource={packages}
              columns={columns}
              rowKey="id"
              loading={isLoading}
              pagination={false}
              bordered
            />
          </Card>

          {/* Edit Package Modal */}
          <Modal
            title={
              <span>
                <EditOutlined style={{ marginRight: 8 }} />
                Edit Electricity Package: {editingPackage?.name}
              </span>
            }
            visible={isEditModalOpen}
            open={isEditModalOpen}
            onCancel={this.handleCloseEditModal}
            footer={[
              <Button key="cancel" onClick={this.handleCloseEditModal}>
                Cancel
              </Button>,
              <Button
                key="save"
                type="primary"
                loading={isSaving}
                onClick={this.handleSavePackage}
              >
                Save Changes
              </Button>,
            ]}
            destroyOnClose
          >
            {validationError && (
              <Alert
                type="error"
                message={validationError}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Form layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="English Name" required>
                    <Input
                      value={formValues.name}
                      onChange={(e) =>
                        this.safeSetState({
                          formValues: { ...formValues, name: e.target.value },
                        })
                      }
                      placeholder="e.g. 50 LYD Electricity"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Arabic Name">
                    <Input
                      value={formValues.arabic_name}
                      onChange={(e) =>
                        this.safeSetState({
                          formValues: { ...formValues, arabic_name: e.target.value },
                        })
                      }
                      placeholder="e.g. كرت كهرباء 50 د.ل"
                      style={{ direction: "rtl", textAlign: "right" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Denomination (LYD)" required>
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      step={1}
                      value={formValues.denomination}
                      onChange={(val) =>
                        this.safeSetState({
                          formValues: { ...formValues, denomination: val },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Active for Merchants"
                    help="Only active packages appear on merchant POS devices"
                  >
                    <Switch
                      checked={formValues.is_active}
                      onChange={(checked) =>
                        this.safeSetState({
                          formValues: { ...formValues, is_active: checked },
                        })
                      }
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Cost Price (LYD)"
                    required
                    help="Upstream provider cost price (3 decimals)"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.001}
                      precision={3}
                      value={formValues.cost_price}
                      onChange={(val) =>
                        this.safeSetState({
                          formValues: { ...formValues, cost_price: val },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Selling Price (LYD)"
                    required
                    help="Price debited from merchant coin balance (3 decimals)"
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.001}
                      precision={3}
                      value={formValues.sell_price}
                      onChange={(val) =>
                        this.safeSetState({
                          formValues: { ...formValues, sell_price: val },
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              {formValues.is_active &&
                Number(formValues.sell_price) < Number(formValues.cost_price) && (
                  <Alert
                    type="warning"
                    message="Price Guard Warning"
                    description="Selling price is lower than cost price. You must set selling price >= cost price before activating this package."
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}

              <div className="order-summary-box" style={{ marginTop: 12 }}>
                <div className="summary-row">
                  <span className="summary-label">Projected Profit per Charge:</span>
                  <span
                    className="summary-value"
                    style={{
                      color:
                        Number(formValues.sell_price) - Number(formValues.cost_price) >= 0
                          ? "#52c41a"
                          : "#ff4d4f",
                    }}
                  >
                    {(Number(formValues.sell_price) - Number(formValues.cost_price)).toFixed(3)}{" "}
                    LYD
                  </span>
                </div>
              </div>
            </Form>
          </Modal>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken:
    state.auth?.authToken ||
    state.auth?.token ||
    state.auth?.admin_token ||
    "",
  loginUserData: state.auth?.loginUserData,
});

export default connect(mapStateToProps)(ElectricityPackages);
